import React, { useCallback, useEffect } from 'react'
import styled from 'styled-components/macro'
import palette from 'src/designSystem/variables/palette'
import { CloseSVG } from 'src/components/Icons/Basic'
import { FlexRow } from 'src/components/Reusable/Flex'
import { FlexAlign } from 'src/components/Reusable/Flex'
import { ChatHistoryList } from 'src/pages/TrusteroIntelligence/chathistorypanel/chatHistoryPanel.components'
import {
  useDeleteChat,
  useListAppCopilotChats,
} from 'src/pages/TrusteroIntelligence/trusteroIntelligence.hooks'
import { useAppCopilotContext } from './AppCopilot.context'

const HistoryPanelOverlay = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  bottom: 8px;
  width: 280px;
  z-index: 2;
  background: ${palette.neutral.white};
  border-radius: 16px;
  border: 1px solid ${palette.neutral[100]};
  box-shadow: -4px 4px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  transition: transform 0.25s ease, opacity 0.25s ease;
  transform: ${({ $open }) =>
    $open ? 'translateX(0)' : 'translateX(calc(100% + 16px))'};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  display: flex;
  flex-direction: column;
`

const HistoryHeader = styled(FlexRow)`
  padding: 12px 16px;
  border-bottom: 1px solid ${palette.neutral[100]};
  flex-shrink: 0;
`

const HistoryTitle = styled.span`
  color: ${palette.neutral[900]};
  font-family: Poppins;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  flex: 1;
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const CloseIcon = styled(CloseSVG)`
  width: 12px;
  height: 12px;
  path {
    fill: ${palette.neutral[600]};
  }
`

const HistoryBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
`

export const CopilotHistoryPanel = (): JSX.Element => {
  const {
    historyOpen,
    setHistoryOpen,
    chatId,
    setChatId,
    setView,
    referringContext,
    writeLastVisitedChat,
  } = useAppCopilotContext()

  const {
    data: chatsData,
    isLoading,
    mutate: reloadChats,
  } = useListAppCopilotChats(referringContext)

  const deleteChatFn = useDeleteChat()

  const chats = chatsData?.getChatsList()

  useEffect(() => {
    if (historyOpen) {
      void reloadChats()
    }
  }, [historyOpen, reloadChats])

  const navigateToChat = useCallback(
    (id: string) => {
      setChatId(id)
      setView('conversation')
      setHistoryOpen(false)
      writeLastVisitedChat(id)
    },
    [setChatId, setView, setHistoryOpen, writeLastVisitedChat],
  )

  const deleteChat = useCallback(
    async (id: string) => {
      await deleteChatFn(id)
      await reloadChats()
      if (id === chatId) {
        setChatId(undefined)
        setView('library')
        writeLastVisitedChat('')
      }
    },
    [
      deleteChatFn,
      reloadChats,
      chatId,
      setChatId,
      setView,
      writeLastVisitedChat,
    ],
  )

  return (
    <HistoryPanelOverlay $open={historyOpen}>
      <HistoryHeader align={FlexAlign.CENTER}>
        <HistoryTitle>Chats</HistoryTitle>
        <CloseButton
          aria-label="Close chat history"
          onClick={() => setHistoryOpen(false)}
        >
          <CloseIcon />
        </CloseButton>
      </HistoryHeader>
      <HistoryBody>
        <ChatHistoryList
          chats={chats}
          activeChatId={chatId}
          navigateToChat={navigateToChat}
          deleteChat={deleteChat}
          isLoading={isLoading ?? false}
          reloadChats={reloadChats ?? (() => void 0)}
        />
      </HistoryBody>
    </HistoryPanelOverlay>
  )
}
